/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';

import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { Box, Grid, Stack } from '@mui/material';
import { useAtom } from 'jotai';
import { useLocation } from 'react-router';

import { useNotification } from '../../../hooks/useNotification/useNotification';
import { useAppInsights } from '../../../providers/AppInsightsProvider/AppInsightsProvider';
import { notificationMessageAtom } from '../../../state/atoms/atoms';
import ResponsiveAppBar from '../../AppBar/AppBar';
import { EventPane } from '../../Event/EventPane/EventPane/EventPane';
import FiltersPane from '../../Filters/FiltersPane/FiltersPane';
import { ActionBar } from '../ActionBar/ActionBar';

export const PageLayout = (props: { children: React.ReactNode }) => {
  const location = useLocation();
  const notifications = useNotification();
  const [notificationMessage] = useAtom(notificationMessageAtom);

  const isMigratePage = location.pathname === '/migrate';
  const isEventPage = location.pathname.includes('/event/');

  const { appInsights } = useAppInsights();

  useEffect(() => {
    if (notificationMessage?.messageBody) {
      const key = notifications.show(notificationMessage.messageBody, {
        severity: notificationMessage.intent,
        autoHideDuration: 3000,
      });

      // Log errors to Application Insights
      if (notificationMessage.intent === 'error') {
        appInsights.trackException({
          exception: new Error(notificationMessage.messageBody),
          severityLevel: SeverityLevel.Error,
          properties: {
            messageTitle: notificationMessage.messageTitle,
            location: location.pathname,
          },
        });
      }
      return () => {
        notifications.close(key);
      };
    }
    return undefined;
  }, [notificationMessage, appInsights, location.pathname]);

  // Event detail page layout
  if (isEventPage) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <ResponsiveAppBar />
        <Stack direction="row">{props.children}</Stack>
      </Box>
    );
  }

  // Migration page layout (full width, no filters/action bar)
  if (isMigratePage) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <ResponsiveAppBar />
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, padding: 2 }}>{props.children}</Box>
      </Box>
    );
  }

  // Default layout (home, calendar, etc.) with filters and action bar
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ResponsiveAppBar />
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, padding: 2 }}>
        <ActionBar />
        <Grid container spacing={2} sx={{ flex: 1 }}>
          <Grid size={{ xs: 0, md: 3 }} sx={{ display: { xs: 'none', md: 'flex' } }}>
            <FiltersPane />
          </Grid>
          <Grid size={{ xs: 12, md: 9 }}>
            {props.children}
            <EventPane />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
